// views/Courses.jsx
import { useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router";
import {
  Container,
  Grid,
  CircularProgress,
  Typography,
  Paper,
  Stack,
  Chip,
  Alert,
  Skeleton,
} from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import axios from "axios";
import customAxios from "lib/axios";
import CourseCard from "components/Courses/CourseCard";
import { useTranslation } from "react-i18next";

const BRAND = "#253e66";

const Shell = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(3),
}));

const Hero = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: 16,
  background:
    "linear-gradient(135deg, rgba(37,62,102,0.12) 0%, rgba(37,62,102,0.06) 100%)",
  boxShadow: "0 4px 18px rgba(0,0,0,0.06)",
  marginBottom: theme.spacing(2),
}));

const Courses = () => {
  const history = useHistory();
  const { t } = useTranslation("courses");

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const userId = localStorage.getItem("user_id");

  const [progressMap, setProgressMap] = useState({});
  const [currentModuleMap, setCurrentModuleMap] = useState({}); // { [courseId]: { es, en } }

  const handleClick = (id) => history.push(`/myCourses/${id}`);

  useEffect(() => {
    let isMounted = true;
    const source = axios.CancelToken.source();

    (async () => {
      try {
        const { data } = await customAxios.get(`/userCourse/user/courses`, {
          cancelToken: source.token,
        });
        if (!isMounted) return;
        setCourses(data);

        const assigned = data.filter((c) => c.assigned);

        // Progreso por curso
        const progressResults = await Promise.all(
          assigned.map((c) =>
            customAxios.get(`/userCourseProgress/user/course/${c.id}`)
          )
        );

        const map = {};
        assigned.forEach((c, i) => {
          map[c.id] = progressResults[i].data;
        });
        if (!isMounted) return;
        setProgressMap(map);

        // Módulos por curso
        const modsResults = await Promise.all(
          assigned.map((c) =>
            customAxios.get(`/modules/${c.id}`, { cancelToken: source.token })
          )
        );

        const cm = {};
        assigned.forEach((c, i) => {
          const modulesList = modsResults[i].data || [];
          const completedCount = map[c.id]?.completed || 0;
          const idx = Math.min(
            completedCount,
            Math.max(modulesList.length - 1, 0)
          );
          const mod = modulesList[idx] || {};
          cm[c.id] = {
            es: mod.title || "",
            en: mod.titleEN || mod.title || "", // fallback a ES si no existe EN
          };
        });
        if (!isMounted) return;
        setCurrentModuleMap(cm);
      } catch (err) {
        if (!axios.isCancel(err)) setError(err);
      } finally {
        if (isMounted) setLoading(false);
      }
    })();

    return () => {
      isMounted = false;
      source.cancel("Component unmounted");
    };
  }, [userId]);

  const assignedCount = useMemo(
    () => courses.filter((c) => c.assigned).length,
    [courses]
  );

  if (loading) {
    return (
      <Shell maxWidth="xl">
        <Hero>
          <Stack
            direction="row"
            spacing={1}
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">{t("loading")}</Typography>
            <Chip
              label="Tradeasy"
              sx={{ bgcolor: alpha(BRAND, 0.15), color: BRAND }}
            />
          </Stack>
        </Hero>

        <Grid container spacing={3}>
          {[...Array(4)].map((_, i) => (
            <Grid item xs={12} md={6} key={i}>
              <Paper sx={{ p: 2, borderRadius: 2 }}>
                <Skeleton
                  variant="rectangular"
                  height={140}
                  sx={{ borderRadius: 1, mb: 2 }}
                />
                <Skeleton width="60%" />
                <Skeleton width="40%" />
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Shell>
    );
  }

  if (error) {
    return (
      <Shell maxWidth="xl">
        <Alert severity="error">{t("error_loading")}</Alert>
      </Shell>
    );
  }

  return (
    <Shell maxWidth="xl">
      <Hero>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
        >
          <Typography variant="h6">{t("hero_title")}</Typography>
          <Stack direction="row" spacing={1}>
            <Chip
              label={`${t("assigned_label")}: ${assignedCount}`}
              sx={{ bgcolor: alpha(BRAND, 0.15), color: BRAND }}
            />
            <Chip
              label={`${t("total_label")}: ${courses.length}`}
              variant="outlined"
              sx={{ borderColor: alpha(BRAND, 0.35) }}
            />
          </Stack>
        </Stack>
      </Hero>

      {courses.length === 0 ? (
        <Paper
          sx={{
            p: 4,
            textAlign: "center",
            borderRadius: 2,
            background: alpha(BRAND, 0.04),
            border: `1px solid ${alpha(BRAND, 0.12)}`,
          }}
        >
          <Typography variant="h6" gutterBottom>
            {t("empty_title")}
          </Typography>
          <Typography color="text.secondary">{t("empty_desc")}</Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {courses.map((course) => {
            const progress = progressMap[course.id] || {
              completed: 0,
              total: 0,
              percentage: 0,
            };
            const cm = currentModuleMap[course.id] || {};
            return (
              <Grid item key={course.id} xs={12} md={6}>
                <CourseCard
                  course={{
                    ...course, // incluye title, titleEN, description, descriptionEN
                    progress,
                    currentModule: cm.es || "",
                    currentModuleEN: cm.en || "",
                  }}
                  onClick={() => handleClick(course.id)}
                />
              </Grid>
            );
          })}
        </Grid>
      )}
    </Shell>
  );
};

export default Courses;
